import {
  LocalTimeGapError,
  instantToLocalDateTime,
  localDateTimeToInstant,
  nextRunDate,
} from "@shared/cron/cronUtils";
import type { ScheduleSummary } from "@shared/types/schedules";
import { timezoneName } from "@/lib/timezones";
import type { ResolvedSchedule, ScheduleDraft } from "@/components/schedules/scheduleForm";

/**
 * Once and Completed on the client (round 10 wave 2, S3 and S5, design §1.5): the server half is
 * `server/services/scheduleDates.ts`. A Once draft holds local dates and 24-hour times in the
 * schedule's zone; the server stores the instants (`runDates`) with no cron, and refuses a time a
 * daylight-saving change skips, which this file says first, on the row.
 */

/** The list's State badge and the editor's status for a schedule with no runs left (S5). */
export const COMPLETED_LABEL = "Completed";
export const COMPLETED_HINT = "Completed: no more runs";

/** A saved schedule that has run all of its dates (`completed`, from the server). */
export function isCompleted(row: Pick<ScheduleSummary, "completed"> | undefined): boolean {
  return row?.completed === true;
}

/** `17:00` → `05:00PM`, the form the server takes. */
function to12h(time24h: string): string {
  const [hour, minute] = time24h.split(":").map(Number) as [number, number];
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}${period}`;
}

/** `05:00PM` → `17:00`. */
function to24h(time12h: string): string {
  const match = /^(\d{2}):(\d{2})(AM|PM)$/i.exec(time12h) as RegExpExecArray;
  const hour12 = Number(match[1]) % 12;
  const hour = match[3]?.toUpperCase() === "PM" ? hour12 + 12 : hour12;
  return `${String(hour).padStart(2, "0")}:${match[2]}`;
}

/** "March 8", read in UTC so the date never moves a day. */
function dayWords(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
  }).format(new Date(`${date}T00:00:00Z`));
}

/** The server's words for a time the spring change skips, or null when the time exists. */
export function runDateGapIssue(date: string, time24h: string, zone: string): string | null {
  try {
    localDateTimeToInstant(date, to12h(time24h), zone);
    return null;
  } catch (error) {
    if (!(error instanceof LocalTimeGapError)) return null;
    return `That time does not exist on ${dayWords(date)} in ${timezoneName(zone)}: the clocks skip that hour. Choose another time.`;
  }
}

/** The instant a row names in `zone`, or null for a time that does not exist or a bad date. */
export function runDateRowInstant(date: string, time24h: string, zone: string): Date | null {
  try {
    return localDateTimeToInstant(date, to12h(time24h), zone);
  } catch {
    return null;
  }
}

/** Whether a row names an instant the saved schedule already holds (`storedRunDates`). */
export function isStoredRunDate(
  date: string,
  time24h: string,
  zone: string,
  stored: readonly string[] | undefined,
): boolean {
  if (!stored || stored.length === 0) return false;
  try {
    const at = localDateTimeToInstant(date, to12h(time24h), zone).getTime();
    return stored.some((value) => new Date(value).getTime() === at);
  } catch {
    return false;
  }
}

/** A saved Once schedule as the draft's fields; null for a cron schedule. */
export function onceDraftFields(
  row: Pick<ScheduleSummary, "cron" | "runDates" | "timezone">,
): Pick<ScheduleDraft, "frequency" | "segment" | "runDates" | "times" | "storedRunDates"> | null {
  if (row.cron || !row.runDates || row.runDates.length === 0) return null;
  return {
    frequency: "Dates",
    segment: "Once",
    times: [],
    storedRunDates: [...row.runDates],
    runDates: row.runDates.map((at) => {
      const local = instantToLocalDateTime(new Date(at), row.timezone);
      return { date: local.date, time: to24h(local.time) };
    }),
  };
}

/** "Mar 6, 2026 at 5:00 PM" in `zone`. */
function whenWords(at: Date, zone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(at)
    .replace(/, (\d{1,2}:\d{2})/, " at $1");
}

/**
 * A Once draft resolved: its instants, the first one left as the next run, and the words. The
 * row issues (`runDateRowIssues`) are passed in so this file needs no value from the form's.
 */
export function resolveOnce(
  draft: ScheduleDraft,
  rowIssues: ReadonlyArray<string | null>,
  now: Date,
): ResolvedSchedule {
  const issue = rowIssues.find((entry) => entry !== null);
  if (issue) return { ok: false, issue };
  const rows = draft.runDates.filter(
    (row): row is { date: string; time: string } => row.date !== null && row.time !== null,
  );
  if (rows.length === 0) return { ok: false, issue: "Choose a date and a time." };
  const instants: Date[] = [];
  for (const row of rows) {
    const gap = runDateGapIssue(row.date, row.time, draft.timezone);
    if (gap) return { ok: false, issue: gap };
    instants.push(localDateTimeToInstant(row.date, to12h(row.time), draft.timezone));
  }
  instants.sort((a, b) => a.getTime() - b.getTime());
  const pausedUntil = draft.pausedUntil ? new Date(draft.pausedUntil) : null;
  const first = instants[0] as Date;
  return {
    ok: true,
    cron: null,
    description:
      instants.length === 1
        ? `Once on ${whenWords(first, draft.timezone)}`
        : `On ${instants.length} dates`,
    nextFireAt: nextRunDate(instants, now, pausedUntil),
    source: "dates",
    runDates: instants.map((at) => at.toISOString()),
  };
}
